import {isArray} from '@shared/helpers/lang'

import {CashReceiptIssueType, STI_INFO} from '@web/core/constants'
import {isPC} from '@web/helpers/userAgent'
import {
    UiEvent,
    uiEventLoggerCreator,
    uiEventAction,
    PageViewEvent,
    createPageViewTargetMap,
    pageViewAction,
    PageViewLoggers,
} from '@web/middlewares/uiLogging'
import {cashReceiptSelector} from '@web/slices/cashReceipt/slice'

import {nClicksMap} from '../../constants/nclicks'

import type {RootState} from '../../interfaces'

const pageViewLoggers: PageViewLoggers = ['lcs']

const CashReceiptMap = {
    [UiEvent.CASH_RECEIPT_CHANGE_INFO]: [
        ['ode.businesscharec', 'ord.businesscharec'],
        ['ode.privatecharec', 'ord.privatecharec'],
    ],
    [UiEvent.CASH_RECEIPT_SAVE_INFO]: [
        ['ode.businessrecsave', 'ord.businessrecsave'],
        ['ode.privaterecsave', 'ord.privaterecsave'],
    ],
}

const uiEventLogger = uiEventLoggerCreator<RootState>({
    nClicksTargetMap: nClicksMap,
    convertNClicksCode: ({state, uiEvent, value}) => {
        const defaultValue = isArray(value) ? (isPC ? value[1] : value[0]) : value
        const {entry} = state.common

        if (uiEvent === UiEvent.CASH_RECEIPT_CHANGE_INFO || uiEvent === UiEvent.CASH_RECEIPT_SAVE_INFO) {
            const {issueType} = cashReceiptSelector(state)
            const caseIndex = issueType.current === CashReceiptIssueType.BUSINESS_REGISTRATION_NUMBER ? 0 : 1
            const deviceIndex = isPC ? 1 : 0
            return CashReceiptMap[uiEvent][caseIndex]?.[deviceIndex] ?? defaultValue
        }

        if (uiEvent === UiEvent.SUBMIT_CLICK_BUTTON) {
            if (entry === 'CHECKOUT') {
                return isPC ? 'ord.payc1' : 'ode.payc1'
            }
            if (entry === 'EASY_BOOKING') {
                return isPC ? 'ord.paybooking' : 'ode.paybooking'
            }
            if (entry === 'NAVER_BLOG') {
                return isPC ? 'ord.payblog' : 'ode.payblog'
            }
            if (entry === 'LOCAL_PAY') {
                return isPC ? 'ord.paylocal' : 'ode.paylocal'
            }
        }

        if (uiEvent === UiEvent.FLOATING_SUBMIT_CLICK_BUTTON) {
            if (entry === 'CHECKOUT') {
                return isPC ? 'ord.payc1_float' : 'ode.payc1_float'
            }
            if (entry === 'EASY_BOOKING') {
                return isPC ? 'ord.paybooking_float' : 'ode.paybooking_float'
            }
            if (entry === 'NAVER_BLOG') {
                return isPC ? 'ord.payblog_float' : 'ode.payblog_float'
            }
            if (entry === 'LOCAL_PAY') {
                return isPC ? 'ord.paylocal_float' : 'ode.paylocal_float'
            }
        }

        if (uiEvent === UiEvent.FLOATING_MODAL_SUBMIT_CLICK_BUTTON) {
            if (entry === 'CHECKOUT') {
                return isPC ? 'ord.payc1_floatmodal' : 'ode.payc1_floatmodal'
            }
            if (entry === 'EASY_BOOKING') {
                return isPC ? 'ord.paybooking_floatmodal' : 'ode.paybooking_floatmodal'
            }
            if (entry === 'NAVER_BLOG') {
                return isPC ? 'ord.payblog_floatmodal' : 'ode.payblog_floatmodal'
            }
            if (entry === 'LOCAL_PAY') {
                return isPC ? 'ord.paylocal_floatmodal' : 'ode.paylocal_floatmodal'
            }
        }
        return defaultValue
    },
    pageViewTargetMap: createPageViewTargetMap([
        [PageViewEvent.ORDER, pageViewLoggers],
        [PageViewEvent.WAITING, pageViewLoggers],
        [PageViewEvent.NON_PURCHASABLE, pageViewLoggers],
    ]),
    pageViewLogger: {
        getLcs: ({target}) => {
            if (target === PageViewEvent.NON_PURCHASABLE) {
                return {
                    sti: isPC ? '' : STI_INFO.ORDER.MO_NON_PURCHASABLE,
                }
            }
            return {
                sti: (function () {
                    if (target === PageViewEvent.BRANCH) {
                        return isPC ? STI_INFO.ORDER.PC_BRANCH : STI_INFO.ORDER.MO_BRANCH
                    } else if (target === PageViewEvent.GIFTING) {
                        return isPC ? STI_INFO.ORDER.PC_GIFTING : STI_INFO.ORDER.MO_GIFTING
                    } else if (target === PageViewEvent.WAITING) {
                        return isPC ? STI_INFO.ORDER.PC_WAITING : STI_INFO.ORDER.MO_WAITING
                    } else if (target === PageViewEvent.PAY_APP) {
                        return STI_INFO.ORDER.PAY_APP
                    }
                    return isPC ? STI_INFO.ORDER.PC : STI_INFO.ORDER.MO
                })(),
            }
        },
    },
})

export const uiEventActions = [uiEventAction.type, pageViewAction.type]

export default uiEventLogger
